import {action, computed, makeObservable, observable} from 'mobx';
import ApiResponseHandler from '@eryxcoop/appyx-comm/src/errors/ApiResponseHandler';
import handleErrorToast from '../../../components/toast/ErrorToast';
import ClosedMonthToExportForm from './ClosedMonthToExportForm';

export default class ListAllowancesFeature {
    constructor(application, navigator) {
        this._application = application;
        this._navigator = navigator;
        this._loaded = false;
        this._allowances = [];
        this._closedMonthToExportForm = undefined;
        this._downloading = false;

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _allowances: observable,
            allowances: computed,
            _closedMonthToExportForm: observable,
            _downloading: observable,
            downloading: computed,
            _finishLoading: action,
            _markDownloadAsStarted: action,
            _markDownloadAsFinished: action,
        });
    }

    async load() {
        await this.loadAllowances();
        const closedMonthOptions = this._closedMonthOptions();
        const previousMonth = closedMonthOptions[0].value;
        this._closedMonthToExportForm = ClosedMonthToExportForm.newFor(closedMonthOptions, previousMonth, this);
        this._finishLoading();
    }

    async loadAllowances() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._allowances = response.allowances();
            },
            handlesError: () => {
                handleErrorToast('No se pudo obtener el listado de bonificaciones.');
            },
        });
        return this._application.apiClient().allowances(responseHandler);
    }

    get loaded() { return this._loaded; }
    get allowances() { return this._allowances; }
    get downloading() { return this._downloading; }

    creditNotesMonthField() {
        return this._closedMonthToExportForm.monthField();
    }

    creditNotesMonthOptions() {
        return this._closedMonthToExportForm.monthOptions();
    }

    _closedMonthOptions() {
        const today = new Date();
        const monthsToOffer = 12;
        return Array.from({length: monthsToOffer}, (_, monthsBeforePreviousMonth) => {
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth() - 1 - monthsBeforePreviousMonth, 1);
            const month = String(firstDayOfMonth.getMonth() + 1).padStart(2, '0');
            const label = firstDayOfMonth.toLocaleDateString('es-AR', {month: 'long', year: 'numeric'});
            return {value: `${firstDayOfMonth.getFullYear()}-${month}`, label: label};
        });
    }

    downloadCreditNotes() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: () => this._markDownloadAsFinished(),
            handlesError: (response) => {
                this._markDownloadAsFinished();
                handleErrorToast(this._downloadErrorMessageFor(response));
            },
        });
        this._markDownloadAsStarted();
        return this._application.apiClient().exportAllowancesCreditNotes(this._closedMonthToExportForm.month(), responseHandler);
    }

    _downloadErrorMessageFor(response) {
        const monthNotYetClosed = response.errors().some(error => error.code === 'month_not_yet_closed');
        const noCreditNotesForMonth = response.errors().some(error => error.code === 'no_allowances_credit_notes_for_month');
        if (monthNotYetClosed) {
            return 'El mes todavía no cerró';
        }
        if (noCreditNotesForMonth) {
            return `No hay NCs de bonificaciones para cargar en ${this._closedMonthToExportForm.month()}`;
        }
        return 'No se pudieron descargar las NCs de bonificaciones';
    }

    _markDownloadAsStarted() {
        this._downloading = true;
    }

    _markDownloadAsFinished() {
        this._downloading = false;
    }

    columns() {
        return [
            {id: 'concept', label: 'Concepto'},
            {id: 'beneficiary', label: 'Beneficiario'},
            {id: 'type', label: 'Tipo'},
            {id: 'validity', label: 'Vigencia'},
        ];
    }

    presentedAllowances() {
        return this._allowances.map((allowance, index) => ({
            id: `${index}`,
            concept: allowance.concept,
            beneficiary: allowance.beneficiary.businessName,
            type: this._presentResolution(allowance.resolution),
            validity: `${allowance.validFrom} → ${allowance.validTo}`,
        }));
    }

    _presentResolution(resolution) {
        if (resolution.kind === 'CREDIT_NOTE_REFUND_ON_ARTICLE') {
            const article = resolution.article;
            return `NC sobre ${article.code} - ${article.name} (${resolution.percentage}%)`;
        }
        if (resolution.kind === 'CREDIT_NOTE_REFUND_ON_ORDER_TOTAL') {
            return `NC sobre total (${resolution.percentage}%)`;
        }
        return resolution.kind;
    }

    _finishLoading() {
        this._loaded = true;
    }
}
